import React, { useState } from 'react';
import { createMMGroup, MMGroup, updateMMGroup } from '../../api/mmode';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import Modal from '../Modal';
import { alertDialog } from '../../utils/dialog';
import SearchSelect from '../SearchSelect';
import { ENABLED_OPTS } from '../../utils/searchOptions';
import { ArchMultiPick } from './archs';

// 加工模式组编辑器 (2026-09-20 从 MModeAdmin 抽出).
//
// 抽出的原因同字段组编辑器: 建设备向导里新建模式值时, 归属组也可能是新的 ——
// 字典页能建组, 向导里却只能从已有组里挑, 就又得中断向导跑一趟字典页。
// 组表单 (编码/中英文名/适用框架/启用) 与保存逻辑收在这里, 字典页与向导共用一份。
// 2026-10-22: 写死的加工头类型 (LASER/KNIFE/THERMAL/INKJET) 改为「适用框架」多选, 选项来自工艺框架注册表.

/** 新建态的组草稿 (默认启用); archCodes 给出时预选 (向导里 = 当前设备所属框架). */
export function newMMGroupDraft(archCodes: string[] = []): Partial<MMGroup> {
    return {
        code: '',
        nameZh: '',
        nameEn: '',
        archCodes,
        isEnabled: true,
    };
}

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

export interface MMGroupEditorModalProps {
    group: Partial<MMGroup>;
    setGroup: (g: Partial<MMGroup>) => void;
    onClose: () => void;
    /** 保存成功 (admin 即时 / 普通用户已提审) 后回调, 带回组编码; 调用方自行刷新清单 */
    onSaved: (code: string) => void;
    /** 是否显示保存按钮 (无写权时只读查看) */
    canSave?: boolean;
    /** 给出时不调接口, 把表单交给调用方 (设备向导: 随套件 newDicts 提交), 此时 onSaved 不会被调用 */
    onDraft?: (group: Partial<MMGroup> & { code: string }) => void;
}

function MMGroupEditorModal({
    group,
    setGroup,
    onClose,
    onSaved,
    canSave = true,
    onDraft,
}: MMGroupEditorModalProps) {
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const code = (group.code || '').trim();
        if (!code) {
            alertDialog('code 不能为空');
            return;
        }
        if (onDraft) {
            onDraft({ ...group, code });
            return;
        }
        setBusy(true);
        try {
            const res = group.id
                ? await updateMMGroup(code, group)
                : await createMMGroup({ ...group, code } as MMGroup);
            onSaved(code);
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={
                group.id ? `编辑加工模式组 · ${group.code}` : '新建加工模式组'
            }
            onClose={onClose}
        >
            <div className="space-y-3">
                <div>
                    <label className="field-label">
                        编码 (业务键, 建后不可改)
                    </label>
                    <input
                        className="inp w-full font-mono"
                        value={group.code || ''}
                        disabled={!!group.id}
                        onChange={(e) =>
                            setGroup({
                                ...group,
                                code: e.target.value,
                            })
                        }
                        placeholder="laser_plane"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">中文名</label>
                        <input
                            className="inp w-full"
                            value={group.nameZh || ''}
                            onChange={(e) =>
                                setGroup({
                                    ...group,
                                    nameZh: e.target.value,
                                })
                            }
                            placeholder="激光平面"
                        />
                    </div>
                    <div>
                        <label className="field-label">英文名</label>
                        <input
                            className="inp w-full"
                            value={group.nameEn || ''}
                            onChange={(e) =>
                                setGroup({
                                    ...group,
                                    nameEn: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="field-label">
                        适用框架 (不选 = 不限; 建设备时按设备所属框架筛模式)
                    </label>
                    <ArchMultiPick
                        value={group.archCodes}
                        onChange={(v) =>
                            setGroup({
                                ...group,
                                archCodes: v,
                            })
                        }
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">启用</label>
                        <SearchSelect
                            className="w-full"
                            value={group.isEnabled === false ? '0' : '1'}
                            onChange={(v) =>
                                setGroup({
                                    ...group,
                                    isEnabled: v === '1',
                                })
                            }
                            options={ENABLED_OPTS}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={save}
                        disabled={busy}
                        hidden={!canSave}
                    >
                        {busy ? '保存中…' : '保存'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default MMGroupEditorModal;
